import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import { makeStyles } from "tss-react/mui";

import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";

import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterIcon from "@mui/icons-material/FilterAlt";
import ListItemIcon from "@mui/material/ListItemIcon";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";

import { Typography as ANTDTypography } from "antd";
const { Paragraph } = ANTDTypography;

const useStyles = makeStyles()((theme) => ({
  menuItem: {
    padding: "0px 8px",
    minWidth: "250px",
    margin: 0,
  },
  smallButton: {
    padding: "0px 8px",
    fontSize: "8px",
  },
  formControl: {
    marginRight: "20px",
  },
  selectElem: {
    minWidth: "120px",
    width: "100%",
    maxWidth: "180px",
    "@media (min-width: 900px)": {
      width: "180px",
    },
    "& .MuiSelect-select": {
      backgroundColor: "transparent",
    },
  },
  button: {
    marginTop: "8px",
  },
  checkBox: {
    "& .MuiSvgIcon-root": {
      fontSize: 24,
    },
    padding: "5px",
    marginRight: "5px",
  },
  filterIcons: {
    color: theme.palette.primary.main,
    cursor: "pointer",
  },
  disabledIcons: {
    cursor: "no-drop",
  },
}));

const CustomPaper = forwardRef((props, ref) => {
  const { children, searchProps = false, ...rest } = props;

  const { search, handleSearchValue, handleClearSearch, handleSearch } = useMemo(() => {
    if (searchProps) return searchProps;
    else
      return {
        search: "",
        handleSearchValue: () => {},
        handleClearSearch: () => {},
        handleSearch: () => {},
      };
  }, [searchProps]);

  return (
    <Paper {...rest} ref={ref}>
      {searchProps ? (
        <Box>
          <TextField
            sx={{ padding: "10px 14px 0px 14px" }}
            variant="standard"
            size="small"
            placeholder="Search"
            fullWidth
            value={search}
            onChange={handleSearchValue}
            InputProps={{
              ...(search && {
                endAdornment: (
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon color="primary" sx={{ fontSize: "0.9rem" }} />
                  </IconButton>
                ),
              }),
            }}
            onKeyDown={handleSearch}
          />
          <hr style={{ margin: "5px" }}></hr>
        </Box>
      ) : (
        <></>
      )}

      {children}
    </Paper>
  );
});

const MultiSelectDropDown = ({
  variant,
  placeHolder,
  options,
  selected,
  basedOn,
  labelOn,
  searchOn = null,
  isSelectAll,
  onSelectChange,
  dataid,
  disabled = false,
  emptySelectionAllowed = false,
  allowSearching = false,
  showIconFor = [],
  icon = <></>,
}) => {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState([]);
  const [disableFilter, setDisableFilter] = useState(false);
  const [toolTipList, setToolTipList] = useState([]);
  const [search, setSearch] = useState("");
  const [finalOptions, setFinalOptions] = useState(options);

  const selectBasedOn = useMemo(() => basedOn || "value", [basedOn]);
  if (!searchOn) searchOn = selectBasedOn;

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      const filteredOptions = options.filter((option) =>
        (option[searchOn] ?? "").toLowerCase().includes(e.target.value.toLowerCase())
      );

      setFinalOptions([...filteredOptions]);
    }
  };

  const handleSearchValue = (e) => {
    setSearch(e.target.value);
  };

  const handleSelectAll = useCallback(() => {
    if (finalOptions.length === tempSelected.length) setTempSelected([]);
    else setTempSelected(finalOptions.map((option) => option[selectBasedOn]));
  }, [finalOptions, selectBasedOn, tempSelected.length]);

  const handleSelectChange = (event) => {
    let { value } = event.target;
    value = value.filter((val) => val !== undefined);

    if (value.find((v) => v === "select all" || v === "clear filter")) return;
    setTempSelected(value);
  };

  const handleClearSearch = useCallback(() => {
    setSearch("");
    setFinalOptions(options);
  }, [options]);

  const handleApply = () => {
    //check selected options get changed and then apply changes
    if (!(selected.length === tempSelected.length && selected.every((elem, i) => elem === tempSelected[i]))) {
      onSelectChange(tempSelected);
      setDisableFilter(isSelectAll && options.length === tempSelected.length);
    }
    setSearch("");
    setFinalOptions(options);
    setOpen(false);
  };

  const handleCancelClick = () => {
    setTempSelected(selected);
    setSearch("");
    setFinalOptions(options);
    setDisableFilter(isSelectAll && options.length === selected.length);
    setOpen(false);
  };

  const handleClearFilter = () => {
    let selectOptions = isSelectAll ? options.map((option) => option[selectBasedOn]) : [];
    setTempSelected(selectOptions);

    if (isSelectAll || emptySelectionAllowed) onSelectChange(selectOptions);

    setDisableFilter(isSelectAll);
    setOpen(false);
    setSearch("");
  };

  const handleSelectOpen = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const SelectIconForFilter = () => {
    if (isSelectAll) {
      if (options.length !== selected.length && !disableFilter)
        return <FilterIcon className={classes.filterIcons} onClick={handleSelectOpen} />;
      else if (open) return <ArrowDropUpIcon className={disabled ? classes.disabledIcons : classes.filterIcons} />;
      return (
        <ArrowDropDownIcon
          className={disabled ? classes.disabledIcons : classes.filterIcons}
          onClick={handleSelectOpen}
        />
      );
    } else {
      if (selected.length > 0 && !disableFilter)
        return <FilterIcon className={classes.filterIcons} onClick={handleSelectOpen} />;
      else if (open) return <ArrowDropUpIcon className={disabled ? classes.disabledIcons : classes.filterIcons} />;
      return (
        <ArrowDropDownIcon
          className={disabled ? classes.disabledIcons : classes.filterIcons}
          onClick={handleSelectOpen}
        />
      );
    }
  };

  useEffect(() => {
    setTempSelected(selected);
    setFinalOptions(options);
    return () => {};
  }, [selected, options]);

  const checked = useMemo(
    () => finalOptions.every((option) => tempSelected.includes(option[selectBasedOn])),
    [finalOptions, selectBasedOn, tempSelected]
  );
  const indeterminate = useMemo(
    () =>
      !checked &&
      tempSelected.length !== 0 &&
      finalOptions.some((option) => tempSelected.includes(option[selectBasedOn])),
    [checked, finalOptions, selectBasedOn, tempSelected]
  );

  return (
    <FormControl className={classes.formControl} data-id={dataid || placeHolder} disabled={disabled}>
      <Select
        multiple
        label={placeHolder || "Select filters"}
        value={tempSelected}
        open={!disabled && open}
        onOpen={handleSelectOpen}
        onClose={() => {
          handleCancelClick();
          setOpen(false);
        }}
        IconComponent={SelectIconForFilter}
        variant={variant || "standard"}
        onChange={handleSelectChange}
        renderValue={() => placeHolder || "Select filters"}
        className={classes.selectElem}
        MenuProps={{
          MenuListProps: {
            style: { paddingTop: "8px", paddingBottom: "0px" },
          },
          PaperProps: {
            style: { borderRadius: "12px", maxHeight: "450px" },
            component: CustomPaper,
            searchProps: allowSearching
              ? {
                  search,
                  handleSearchValue,
                  handleClearSearch,
                  handleSearch,
                }
              : false,
          },
        }}
        displayEmpty={!isSelectAll}
      >
        <MenuItem
          className={classes.menuItem}
          value="clear filter"
          disabled={(isSelectAll ? options.length === selected.length : selected.length === 0) || disableFilter}
          onClick={(e) => handleClearFilter()}
        >
          <ListItemIcon className={classes.checkBox}>
            <FilterAltOffIcon className={classes.filterIcons} />
          </ListItemIcon>
          <ListItemText primary="Clear Filter" />
        </MenuItem>
        <hr style={{ margin: "5px" }}></hr>
        <MenuItem
          key={`Value_select_all`}
          value="select all"
          className={classes.menuItem}
          sx={{ padding: 0, display: "flex", alignItems: "center", height: "100%" }}
          onClick={handleSelectAll}
        >
          <Checkbox checked={checked} indeterminate={indeterminate} className={classes.checkBox} />
          <Paragraph style={{ width: "200px", margin: 0, fontFamily: "inherit" }}>Select All</Paragraph>
        </MenuItem>
        {finalOptions.map((option, index) => {
          const visibleIcon = showIconFor?.includes(option[selectBasedOn]) ? icon || null : null;

          return (
            <MenuItem
              key={`Value_${option[selectBasedOn]}`}
              value={option[selectBasedOn]}
              className={classes.menuItem}
              sx={{ padding: 0, display: "flex", alignItems: "center", height: "100%" }}
            >
              <Checkbox checked={tempSelected.indexOf(option[selectBasedOn]) > -1} className={classes.checkBox} />
              <Tooltip
                title={
                  <Typography variant="body2">{option[labelOn] ?? (option.Name || option.label || "")}</Typography>
                }
                disableHoverListener={!toolTipList.some((opt) => opt === option[selectBasedOn])}
                arrow
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Paragraph
                    ellipsis={{
                      rows: 1,
                      onEllipsis: (isEllipsis) => {
                        if (isEllipsis) setToolTipList([...toolTipList, option[selectBasedOn]]);
                        else setToolTipList([...toolTipList.map((opt) => opt !== option[selectBasedOn])]);
                      },
                    }}
                    style={{
                      ...(visibleIcon === null ? { width: "200px" } : { maxWidth: "178px" }),
                      margin: 0,
                      fontFamily: "inherit",
                    }}
                  >
                    {option[labelOn] ?? (option.Name || option.label || "")}
                  </Paragraph>
                  {visibleIcon || <></>}
                </Stack>
              </Tooltip>
            </MenuItem>
          );
        })}
        <Paper elevation={2} sx={{ position: "sticky", bottom: "0" }}>
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", p: 0, m: "0 8px" }}>
            <Button onClick={handleCancelClick} className={classes.smallIconButton}>
              Cancel
            </Button>
            <Button
              color="success"
              onClick={handleApply}
              className={classes.smallIconButton}
              sx={{ marginLeft: "10px" }}
              disabled={tempSelected.length === 0 && !emptySelectionAllowed}
            >
              Apply
            </Button>
          </Box>
        </Paper>
      </Select>
    </FormControl>
  );
};

export default MultiSelectDropDown;
